import {messages} from '@smartmixin/i18n';
import {MessageDescriptor} from 'react-intl';
import * as Dates from '../Dates';

export * from './allTime';

/**
 * `coverage` as served by `GET stations/{id}/records`.
 *
 * Two fields describe depth and only one of them is safe to show. `n_years` is
 * built as `len(unique(index.year))` — the number of distinct calendar years
 * the record *touches*, which a single day either side of New Year inflates to
 * two. `n_days` is the row count of daily summaries, so gaps shrink it instead
 * of inflating it.
 */
export type RecordsCoverage = {
  first_date?: string | null;
  last_date?: string | null;
  n_days?: number | null;
  n_years?: number | null;
};

export type DepthValues = {days: number} | {months: number} | {years: number};

export type RecordsDepth = {
  descriptor: MessageDescriptor;
  values: DepthValues;
};

/** Mean Gregorian month, so the months bucket does not drift across a year. */
const DAYS_PER_MONTH = 30.4375;
/**
 * 365, not the mean-Gregorian 365.25. A gapless record of N calendar years
 * holds `365N + leapDays`, and leap days only reach N/4 — so flooring by
 * 365.25 loses a whole year whenever the window starts after a leap year.
 * Two full years (2021-01-01..2022-12-31) is 730 days, which /365.25 floors
 * to 1 and drops into the months bucket as "24 months of data" — exactly the
 * apparent-downgrade MIN_YEARS_FOR_YEARS exists to prevent. Dividing by 365
 * cannot overstate either: floor((365N + L)/365) === N for any L < 365.
 */
const DAYS_PER_YEAR = 365;

/** Below this, a day count is more informative than "0 months". */
export const MIN_DAYS_FOR_MONTHS = 60;
/** Years only once two whole years of days exist — "1 year" would read as a
 * downgrade from the "24 months" immediately below it. */
export const MIN_YEARS_FOR_YEARS = 2;

/**
 * Describe how much history is behind a station's records, escalating
 * days → months → years as the record grows.
 *
 * Always derived from `n_days`. Deriving the years bucket from `n_years`
 * instead leaves sparse historical records claiming decades they do not have:
 * production station 76416 holds 5,579 daily summaries scattered across 55
 * calendar years, and 1,177 stations overstate by more than two years that way.
 *
 * Returns `undefined` when there is no usable coverage, so callers can drop the
 * label rather than render a zero.
 */
export const describeDepth = (
  coverage?: RecordsCoverage | null,
): RecordsDepth | undefined => {
  const days = coverage?.n_days;
  if (typeof days !== 'number' || !Number.isFinite(days) || days <= 0) {
    return undefined;
  }

  const years = Math.floor(days / DAYS_PER_YEAR);
  if (years >= MIN_YEARS_FOR_YEARS) {
    // Floor, never round: the label must not claim a year the record has not
    // completed.
    return {descriptor: messages.reports.yearsOfData, values: {years}};
  }

  if (days >= MIN_DAYS_FOR_MONTHS) {
    return {
      descriptor: messages.reports.monthsOfData,
      values: {months: Math.round(days / DAYS_PER_MONTH)},
    };
  }

  return {descriptor: messages.reports.daysOfData, values: {days}};
};

/**
 * Fallback for the days a calendar month needs before its average and ranking
 * are published. The live value ships in `baseline_meta.min_days_valid_month`
 * (backend `MIN_DAYS_VALID_MONTH`); this is only used when a payload predates
 * that key, so the copy cannot silently drift from the backend gate.
 */
export const DEFAULT_REQUIRED_DAYS_FOR_MONTH = 20;

export type RecordsNextUnlock =
  | {
      kind: 'monthCountdown';
      daysRecorded: number;
      daysRemaining: number;
      requiredDays: number;
    }
  | {kind: 'monthRule'; requiredDays: number};

const monthOf = (isoDate?: string | null): [number, number] | undefined => {
  if (!isoDate) {
    return undefined;
  }
  const [year, month] = isoDate.split('-').map(Number);
  return Number.isFinite(year) && Number.isFinite(month)
    ? [year, month]
    : undefined;
};

/** The gate counts days carrying a mean temperature, so a station with no
 * temperature at all can never reach it — no countdown is honest there. */
const hasTemperature = (
  allTime?: Record<string, {value?: number} | undefined> | null,
) => Boolean(allTime?.T_MAX || allTime?.T_MIN);

/**
 * Describe the next thing a thin records report will gain, if anything.
 *
 * `monthCountdown` is returned only when the number is genuinely defensible.
 * A month's average unlocks on days *in that calendar month* and the backend
 * publishes no per-month day count below the gate, so the only derivable case
 * is a station whose entire record sits inside the current calendar month,
 * where `coverage.n_days` stands in for that month's count. On top of that the
 * month must still be able to reach the gate: on the 27th of a 31-day month
 * only 5 days remain, and telling a station with 4 days recorded that it needs
 * 16 more "this month" is arithmetic no calendar can satisfy. Measured on
 * production, that check moves 53 of 64 candidate stations to `monthRule`.
 *
 * Everything else gets `monthRule`, which states the threshold without
 * inventing a number. Returns `undefined` once any month has reached the gate,
 * because monthly averages are then already on screen.
 *
 * Caveat, deliberately accepted: `coverage.n_days` counts daily-summary rows
 * while the gate counts rows with a mean temperature, so a row lacking T_AVG
 * makes the countdown read one short. On production 8 of the 22 comparable
 * stations differ, always by one. The `hasTemperature` guard removes the
 * unbounded case (a station that would count down forever); the residual
 * off-by-one needs a backend-published month count to fix properly.
 */
export const describeNextUnlock = (
  records:
    | {
        coverage?: RecordsCoverage | null;
        month_years?: Record<string, unknown> | null;
        all_time?: Record<string, {value?: number} | undefined> | null;
        baseline_meta?: Record<string, unknown> | null;
      }
    | undefined
    | null,
  timezone: string,
): RecordsNextUnlock | undefined => {
  const coverage = records?.coverage;
  if (!coverage) {
    return undefined;
  }
  if (Object.keys(records?.month_years ?? {}).length > 0) {
    return undefined;
  }

  const published = records?.baseline_meta?.min_days_valid_month;
  const requiredDays =
    typeof published === 'number' &&
    Number.isInteger(published) &&
    published > 0
      ? published
      : DEFAULT_REQUIRED_DAYS_FOR_MONTH;
  const rule = {kind: 'monthRule', requiredDays} as const;

  const daysRecorded = coverage.n_days;
  if (!Number.isInteger(daysRecorded) || (daysRecorded as number) <= 0) {
    return undefined;
  }

  if (!hasTemperature(records?.all_time)) {
    return rule;
  }

  const first = monthOf(coverage.first_date);
  const last = monthOf(coverage.last_date);
  if (!first || !last || first[0] !== last[0] || first[1] !== last[1]) {
    return rule;
  }
  if (
    first[0] !== Dates.currentYear(timezone) ||
    first[1] !== Dates.currentMonth(timezone)
  ) {
    // The record is complete but sits in a month that has ended: counting down
    // to a gate that can no longer be reached would be a lie.
    return rule;
  }

  const daysRemaining = requiredDays - (daysRecorded as number);
  if (daysRemaining <= 0) {
    return rule;
  }

  // ...and the month must still have room for them.
  const daysLeftInMonth =
    Dates.daysInMonth(first[0], first[1]) -
    Dates.currentDayOfMonth(timezone) +
    1;
  if (daysRemaining > daysLeftInMonth) {
    return rule;
  }

  return {
    kind: 'monthCountdown',
    daysRecorded: daysRecorded as number,
    daysRemaining,
    requiredDays,
  };
};
